import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canConvertImageToJpeg, ImageColorSpace } from '../../utils/imageConversion';
import {
  validateFeedMedia,
  validateImage,
  validateReelsMedia,
  validateStoryMedia,
  ValidationResult,
} from '../../utils/mediaValidation';

interface UploadElementWithClear extends HTMLElement {
  deleteAllFiles: () => Promise<void>;
}

interface UseUploadHandlerProps {
  publicationType: string;
  onValidationChange?: (isValid: boolean, file: File | null, validation: ValidationResult | null) => void;
}

const mediaValidators = {
  stories: validateStoryMedia,
  feed: validateFeedMedia,
  reels: validateReelsMedia,
};

const isImageUpload = (publicationType: string, file: File): boolean =>
  ['feed', 'stories'].includes(publicationType) &&
  (file.type.toLowerCase().startsWith('image/') || canConvertImageToJpeg(file));

export const useUploadHandler = ({ publicationType, onValidationChange }: UseUploadHandlerProps) => {
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [selectedImagePreviewUrl, setSelectedImagePreviewUrl] = useState<string | null>(null);
  const [feedbackFileName, setFeedbackFileName] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showFallbackUpload, setShowFallbackUpload] = useState(false);
  const rejectedClearCount = useRef(0);
  const validationRequestId = useRef(0);
  const selectedFileRef = useRef<File | null>(null);
  const selectedImagePreviewUrlRef = useRef<string | null>(null);
  const handledInputFilesRef = useRef(new WeakSet<File>());
  const { t } = useTranslation();

  const clearSelectedImagePreview = () => {
    if (selectedImagePreviewUrlRef.current) {
      URL.revokeObjectURL(selectedImagePreviewUrlRef.current);
      selectedImagePreviewUrlRef.current = null;
    }
    setSelectedImagePreviewUrl(null);
  };

  const updateSelectedFile = (file: File | null) => {
    clearSelectedImagePreview();
    selectedFileRef.current = file;
    setSelectedFile(file);
  };

  const createSelectedImagePreview = (file: File) => {
    if (!file.type.toLowerCase().startsWith('image/')) return;

    const previewUrl = URL.createObjectURL(file);
    selectedImagePreviewUrlRef.current = previewUrl;
    setSelectedImagePreviewUrl(previewUrl);
  };

  useEffect(() => () => {
    if (selectedImagePreviewUrlRef.current) {
      URL.revokeObjectURL(selectedImagePreviewUrlRef.current);
    }
  }, []);

  const createGenericErrorResult = (): ValidationResult => ({
    isValid: false,
    errors: [t('errors.genericError')],
    warnings: [],
  });

  const clearRejectedUpload = async () => {
    const uploadElement = document.getElementById(`upload-${publicationType}`) as UploadElementWithClear | null;
    if (!uploadElement || typeof uploadElement.deleteAllFiles !== 'function') {
      return;
    }

    rejectedClearCount.current += 1;
    try {
      await uploadElement.deleteAllFiles();
    } finally {
      rejectedClearCount.current -= 1;
    }
  };

  const validateImageFile = async (
    file: File,
    requestId: number,
    colorSpace: ImageColorSpace = 'unknown',
  ) => {
    const context = publicationType as 'feed' | 'stories';
    setIsValidating(true);
    try {
      const validation = await validateImage(file, context, { t, colorSpace });
      if (requestId !== validationRequestId.current) return;

      if (validation.requiresImageEdit && canConvertImageToJpeg(file)) {
        setPendingImageFile(file);
        setValidationResult(validation);
        return;
      }
      await applyValidationResult(file, validation, requestId);
    } catch (error) {
      console.error('Erro durante validação da imagem:', error);
      await applyValidationResult(file, createGenericErrorResult(), requestId);
    } finally {
      if (requestId === validationRequestId.current) setIsValidating(false);
    }
  };

  const validateSelectedFile = async (file: File, requestId: number) => {
    const validateMedia = mediaValidators[publicationType as keyof typeof mediaValidators];
    if (!validateMedia) {
      setValidationResult(null);
      onValidationChange?.(true, file, null);
      return;
    }

    setIsValidating(true);
    try {
      const result = await validateMedia(file, t);
      await applyValidationResult(file, result, requestId);
    } catch (error) {
      console.error('Erro durante validação do arquivo:', error);
      await applyValidationResult(file, createGenericErrorResult(), requestId);
    } finally {
      if (requestId === validationRequestId.current) setIsValidating(false);
    }
  };

  const applyValidationResult = async (file: File, validation: ValidationResult, requestId: number) => {
    if (requestId !== validationRequestId.current) {
      return;
    }

    if (!validation.isValid) {
      updateSelectedFile(null);
      setFeedbackFileName(file.name);
      setValidationResult(validation);
      onValidationChange?.(false, null, validation);
      try {
        await clearRejectedUpload();
      } catch (error) {
        console.error('Erro ao remover arquivo rejeitado:', error);
      }
      return;
    }

    updateSelectedFile(file);
    createSelectedImagePreview(file);
    setFeedbackFileName(file.name);
    setValidationResult(validation);
    onValidationChange?.(true, file, validation);
  };

  const clearSelection = () => {
    updateSelectedFile(null);
    setPendingImageFile(null);
    setFeedbackFileName(null);
    setValidationResult(null);
    setIsValidating(false);
    onValidationChange?.(false, null, null);
  };

  const handleFileSelect = async (files: FileList | null) => {
    const requestId = ++validationRequestId.current;
    if (!files || files.length === 0) {
      clearSelection();
      return;
    }

    const file = files[0];
    updateSelectedFile(file);
    setPendingImageFile(null);
    setFeedbackFileName(file.name);
    onValidationChange?.(false, null, null);

    if (isImageUpload(publicationType, file)) {
      await validateImageFile(file, requestId);
      return;
    }

    await validateSelectedFile(file, requestId);
  };

  const confirmImage = async (file: File, colorSpace: ImageColorSpace = 'unknown') => {
    setPendingImageFile(null);
    updateSelectedFile(file);
    setFeedbackFileName(file.name);
    await validateImageFile(file, validationRequestId.current, colorSpace);
  };

  const setupUploadListeners = () => {
    const handleFileChange = (event: any) => {
      let files = null;

      if (event.detail?.value && Array.isArray(event.detail.value)) {
        const fileList = new DataTransfer();
        for (const file of event.detail.value) {
          fileList.items.add(file);
        }
        files = fileList.files;
      } else if (event.detail?.files) {
        files = event.detail.files;
      } else if (Array.isArray(event.detail)) {
        const fileList = new DataTransfer();
        for (const file of event.detail) {
          fileList.items.add(file);
        }
        files = fileList.files;
      } else {
        files = event.target.files;
      }

      if (rejectedClearCount.current > 0 && (!files || files.length === 0)) {
        return;
      }
      handleFileSelect(files);
    };

    const handleError = () => {
      validationRequestId.current += 1;
      setIsValidating(false);
      const errorResult = {
        isValid: false,
        errors: [t('validation.fileErrorDescription')],
        warnings: [],
      };
      updateSelectedFile(null);
      setPendingImageFile(null);
      setFeedbackFileName(null);
      setValidationResult(errorResult);
      onValidationChange?.(false, null, errorResult);
    };

    const handleInputChange = (e: any) => {
      if (e.target.files && e.target.files.length > 0) {
        handledInputFilesRef.current.add(e.target.files[0]);
        handleFileSelect(e.target.files);
      }
    };

    const handleInputChangeRaw = (e: any) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        if (handledInputFilesRef.current.delete(file)) {
          return;
        }
        setTimeout(() => {
          if (selectedFileRef.current !== file) {
            setValidationResult({
              isValid: false,
              errors: [`Arquivo "${file.name}" pode ser muito grande para o componente de upload.`],
              warnings: [],
            });
            setShowFallbackUpload(true);
          }
        }, 1000);
      }
    };

    const setupListeners = () => {
      const uploadId = `upload-${publicationType}`;
      let uploadElement = document.getElementById(uploadId);
      uploadElement ??= document.querySelector('bds-upload');

      if (!uploadElement) {
        return null;
      }

      uploadElement.addEventListener('bdsUploadChange', handleFileChange);
      uploadElement.addEventListener('bdsUploadDelete', () => {
        if (rejectedClearCount.current > 0) {
          return;
        }
        validationRequestId.current += 1;
        setIsValidating(false);
        updateSelectedFile(null);
        setPendingImageFile(null);
        setFeedbackFileName(null);
        setValidationResult(null);
        onValidationChange?.(false, null, null);
      });

      uploadElement.addEventListener('bdsError', handleError);
      uploadElement.addEventListener('error', handleError);

      const findAndSetupInput = () => {
        const selectors = ['input[type="file"]', 'input[type=file]', 'input', '[type="file"]'];

        let inputElement: HTMLInputElement | null = null;
        for (const selector of selectors) {
          inputElement = uploadElement.querySelector<HTMLInputElement>(selector);
          if (inputElement?.type === 'file') {
            break;
          }
        }

        if (inputElement && !inputElement.dataset?.listenerAdded) {
          inputElement.dataset.listenerAdded = 'true';
          inputElement.addEventListener('change', handleInputChange);
          inputElement.addEventListener('change', handleInputChangeRaw);
          return true;
        }
        return false;
      };

      const foundInput = findAndSetupInput();

      const observer = new MutationObserver(() => {
        if (!foundInput) {
          findAndSetupInput();
        }
      });

      observer.observe(uploadElement, { childList: true, subtree: true });

      return {
        cleanup: () => {
          uploadElement.removeEventListener('bdsUploadChange', handleFileChange);
          uploadElement.removeEventListener('bdsError', handleError);
          uploadElement.removeEventListener('error', handleError);
          observer.disconnect();
        },
      };
    };

    return setupListeners();
  };

  const editSelectedImage = useCallback(() => {
    if (selectedFile && isImageUpload(publicationType, selectedFile)) {
      setPendingImageFile(selectedFile);
    }
  }, [publicationType, selectedFile]);
  const imageContext: 'feed' | 'stories' = publicationType === 'feed' ? 'feed' : 'stories';

  return {
    validationResult,
    selectedFile,
    editSelectedImage,
    pendingImageFile,
    selectedImagePreviewUrl,
    confirmImage,
    imageContext,
    feedbackFileName,
    isValidating,
    showFallbackUpload,
    setupUploadListeners,
  };
};
